import { WhiteLayerMaskMode, type Sticker, type WhiteLayerMask } from './types';

export async function prepareCombinedMasks(masks: WhiteLayerMask[]): Promise<WhiteLayerMask[]> {
  const processedMasks = await Promise.all(
    masks.map(async (mask) => {
      console.log(mask.mode);
      if (mask.mode === WhiteLayerMaskMode.Full) {
        mask.maskImgUrl = mask.maskImgUrl.length ? await generateEffectMask(mask.maskImgUrl) : '';
      } else if (mask.mode === WhiteLayerMaskMode.None) {
        mask.maskImgUrl = mask.maskImgUrl.length ? await imageToBase64(mask.maskImgUrl) : '';
      }
      return mask;
    }),
  );

  return processedMasks;
}

async function imageToBase64(imgUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Avoid CORS issues
    img.src = imgUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context is not supported.'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      // Get image pixel data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]; // Red
        const g = data[i + 1]; // Green
        const b = data[i + 2]; // Blue

        // Detect black areas
        if (!isPixelWhite(r, g, b)) {
          // Make black areas transparent
          data[i + 3] = 0;
        }
      }

      // Put the modified image back onto the canvas
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png')); // Convert to Base64
    };

    img.onerror = () => reject(new Error('Failed to load image.'));
  });
}

export async function generateEffectMask(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas 2D context is not available.'));
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            if (isPixelWhite(r, g, b)) {
              data[i + 3] = 0; // Transparent
            } else {
              data[i] = 255;
              data[i + 1] = 255;
              data[i + 2] = 255;
              data[i + 3] = 255;
            }
          }

          ctx.putImageData(imageData, 0, 0);

          try {
            const base64Image = canvas.toDataURL('image/png');
            resolve(base64Image);
          } catch (dataUrlError) {
            reject(new Error('Failed to convert canvas to image: ' + dataUrlError));
          }
        } catch (processError) {
          reject(new Error('Error processing image: ' + processError));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image from URL: ' + imageUrl));
      };
    } catch (err) {
      reject(new Error('Unexpected error: ' + err));
    }
  });
}

function isPixelWhite(r: number, g: number, b: number): boolean {
  return r > 200 && g > 200 && b > 200;
}

export function base64ToBlob(base64Data: string, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(base64Data.split(',')[1]); // Decoding base64
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}

export function mergeActiveMaskModes(
  sticker: Sticker,
  whiteLayerMasks: WhiteLayerMask[],
): WhiteLayerMask[] {
  const currentMasks = sticker.whiteLayerMasks;

  if (!currentMasks) {
    return whiteLayerMasks; // No current masks to compare with, return the original array.
  }

  const alteredWhiteLayerMasks = whiteLayerMasks.map((mask) => {
    // Find a close match from currentMasks based on colorRange.
    const currentMask = currentMasks.find((cm) =>
      isColorRangeClose(cm.colorRange, mask.colorRange, 20),
    );

    return {
      ...mask,
      // Use the matched colorRange or retain the original.
      colorRange: currentMask?.colorRange ?? mask.colorRange,
      // Update the mode only if a matching currentMask is found.
      mode: currentMask ? currentMask.mode : mask.mode,
    };
  });
  return alteredWhiteLayerMasks;
}

function isColorRangeClose(
  colorRange1: string,
  colorRange2: string,
  tolerance: number = 10,
): boolean {
  if (!colorRange1 || !colorRange2) {
    return false;
  }
  const components1 = colorRange1.split(',').map(Number);
  const components2 = colorRange2.split(',').map(Number);

  if (components1.length !== components2.length) {
    return false; // Color ranges must have the same number of components.
  }

  return components1.every((value, index) => Math.abs(value - components2[index]) <= tolerance);
}
